import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
    COMMAND_PRIORITY_HIGH,
    PASTE_COMMAND,
    $getSelection,
    $isRangeSelection,
    $createTextNode,
    $insertNodes,
    $getRoot,
    $isElementNode,
} from "lexical";
import { $createLinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import { useEffect, useState } from "react";
import { isUrl, normalizeUrl, isSafeUrl, urlMetadataCache } from "../../../utils/linkUnfurling";
import { config } from "../../../config";

// Titles like "- YouTube" or "Vimeo" mean the backend couldn't extract a real
// page title (scraper blocked). Keep the URL as the link text in that case.
const isUsableTitle = (title, url) => {
    if (!title || typeof title !== 'string') return false;
    const trimmed = title.trim();
    if (!trimmed) return false;
    const normalized = trimmed.replace(/^[-\s|·•]+|[-\s|·•]+$/g, '').trim();
    if (!normalized) return false;
    const junk = new Set(['youtube', 'vimeo', 'twitter', 'x', 'facebook']);
    if (junk.has(normalized.toLowerCase())) return false;
    try {
        if (normalized.toLowerCase() === new URL(url).hostname.replace(/^www\./, '').toLowerCase()) {
            return false;
        }
    } catch (e) {}
    return true;
};

/**
 * Fetch URL metadata from the backend
 * @param {string} url - URL to fetch metadata for
 * @returns {Promise<Object|null>} - Metadata object or null if failed
 */
const fetchUrlMetadata = async (url) => {
  // Check cache first
  const cached = urlMetadataCache.get(url);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(`${config.API_URI}/v1/url-metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ url })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch metadata');
    }

    const metadata = await response.json();
    
    // Cache the result
    urlMetadataCache.set(url, metadata);
    
    return metadata;
  } catch (error) {
    console.error('Error fetching URL metadata:', error);
    return null;
  }
};



/**
 * Enhanced Link Unfurling Plugin that provides:
 * 1. Auto-link creation when pasting URLs over selected text
 * 2. Smart URL detection and normalization
 * 3. Automatic URL unfurling with page titles
 * 4. Video embed detection
 */
export default function LinkUnfurlingPlugin() {
    const [editor] = useLexicalComposerContext();
    const [pendingUrls, setPendingUrls] = useState(new Set());



    useEffect(() => {
        return mergeRegister(
            // Handle paste events for URL over selected text
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    const selection = $getSelection();
                    
                    // Only handle if we have a range selection (text selected)
                    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
                        return false;
                    }

                    const clipboardData = event.clipboardData || window['clipboardData'];
                    if (!clipboardData) return false;

                    const pastedText = clipboardData.getData('text/plain');

                    // Check if pasted text is a URL
                    if (isUrl(pastedText)) {
                        event.preventDefault();

                        // Normalize the URL
                        const normalizedURL = normalizeUrl(pastedText);
                        const originalUrl = pastedText;

                        // Create link with the selected text using editor.update
                        editor.update(() => {
                            const currentSelection = $getSelection();
                            if ($isRangeSelection(currentSelection) && !currentSelection.isCollapsed()) {
                                // Apply the link to the current selection
                                editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedURL);
                                
                                // Start unfurling process for the new link
                                if (isSafeUrl(normalizedURL)) {
                                    setPendingUrls(prev => new Set(prev).add(normalizedURL));
                                    
                                    // Fetch metadata and update link text with a delay
                                    setTimeout(() => {
                                        fetchUrlMetadata(normalizedURL).then(metadata => {
                                            console.log('LinkUnfurling: Metadata received:', metadata);
                                            if (metadata && isUsableTitle(metadata.title, normalizedURL)) {
                                                editor.update(() => {
                                                // Find all link nodes with this URL and update their text
                                                const root = $getRoot();
                                                const linkNodes = [];

                                                function findLinkNodes(node) {
                                                    if ($isLinkNode(node) && node.getURL() === normalizedURL) {
                                                        linkNodes.push(node);
                                                    }
                                                    // Only traverse children if the node is an element node
                                                    if ($isElementNode(node)) {
                                                        const children = node.getChildren();
                                                        children.forEach(child => findLinkNodes(child));
                                                    }
                                                }

                                                findLinkNodes(root);
                                                console.log('LinkUnfurling: Found link nodes:', linkNodes.length);

                                                linkNodes.forEach(linkNode => {
                                                    const textNode = linkNode.getFirstChild();
                                                    const currentText = textNode?.getTextContent();
                                                    console.log('LinkUnfurling: Text node content:', currentText);
                                                    console.log('LinkUnfurling: Comparing with URL:', normalizedURL);

                                                    // Update if text matches URL or if it's a URL-like text
                                                    if (textNode && (currentText === normalizedURL ||
                                                                   currentText === originalUrl ||
                                                                   currentText?.startsWith('http'))) {
                                                        console.log('LinkUnfurling: Updating text to:', metadata.title);
                                                        textNode.setTextContent(metadata.title);
                                                    }
                                                });
                                            });
                                        }

                                        setPendingUrls(prev => {
                                            const newSet = new Set(prev);
                                            newSet.delete(normalizedURL);
                                            return newSet;
                                        });
                                    }).catch(error => {
                                        console.error('LinkUnfurling: Error fetching metadata:', error);
                                        setPendingUrls(prev => {
                                            const newSet = new Set(prev);
                                            newSet.delete(normalizedURL);
                                            return newSet;
                                        });
                                    });
                                }, 100); // Small delay to let the link be created first
                                }
                            } else {
                                console.log('LinkUnfurlingPlugin: No valid selection found');
                            }
                        });

                        return true;
                    }

                    return false;
                },
                COMMAND_PRIORITY_HIGH
            ),

            // Handle direct URL typing/pasting without selection
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    const selection = $getSelection();
                    
                    // Only handle if we have a collapsed selection (cursor position)
                    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                        return false;
                    }

                    const clipboardData = event.clipboardData || window['clipboardData'];
                    if (!clipboardData) return false;

                    const pastedText = clipboardData.getData('text/plain').trim();

                    // Check if pasted text is a standalone URL
                    if (isUrl(pastedText) && pastedText.split(/\s+/).length === 1) {
                        event.preventDefault();

                        const normalizedURL = normalizeUrl(pastedText);

                        editor.update(async () => {
                            const currentSelection = $getSelection();
                            if ($isRangeSelection(currentSelection)) {
                                // Check if URL is safe before processing
                                if (!isSafeUrl(normalizedURL)) {
                                    // Just insert as plain text if unsafe
                                    const textNode = $createTextNode(pastedText);
                                    $insertNodes([textNode]);
                                    return;
                                }

                                // Add to pending URLs
                                setPendingUrls(prev => new Set(prev).add(normalizedURL));

                                // Create a temporary link with the URL as text
                                const linkNode = $createLinkNode(normalizedURL);
                                const textNode = $createTextNode(normalizedURL);
                                linkNode.append(textNode);
                                $insertNodes([linkNode]);

                                // Fetch metadata and update link text with a delay
                                setTimeout(() => {
                                    fetchUrlMetadata(normalizedURL).then(metadata => {
                                        console.log('LinkUnfurling (standalone): Metadata received:', metadata);
                                        if (metadata && isUsableTitle(metadata.title, normalizedURL)) {
                                            editor.update(() => {
                                            // Find all link nodes with this URL and update their text
                                            const root = $getRoot();
                                            const linkNodes = [];

                                            function findLinkNodes(node) {
                                                if ($isLinkNode(node) && node.getURL() === normalizedURL) {
                                                    linkNodes.push(node);
                                                }
                                                // Only traverse children if the node is an element node
                                                if ($isElementNode(node)) {
                                                    const children = node.getChildren();
                                                    children.forEach(child => findLinkNodes(child));
                                                }
                                            }

                                            findLinkNodes(root);
                                            console.log('LinkUnfurling (standalone): Found link nodes:', linkNodes.length);

                                            linkNodes.forEach(linkNode => {
                                                const textNode = linkNode.getFirstChild();
                                                const currentText = textNode?.getTextContent();
                                                console.log('LinkUnfurling (standalone): Text node content:', currentText);
                                                console.log('LinkUnfurling (standalone): Comparing with URL:', normalizedURL);

                                                // Update if text matches URL or if it's a URL-like text
                                                if (textNode && (currentText === normalizedURL ||
                                                               currentText === pastedText ||
                                                               currentText?.startsWith('http'))) {
                                                    console.log('LinkUnfurling (standalone): Updating text to:', metadata.title);
                                                    textNode.setTextContent(metadata.title);
                                                }
                                            });
                                        });
                                    }

                                    setPendingUrls(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(normalizedURL);
                                        return newSet;
                                    });
                                }).catch(error => {
                                    console.error('LinkUnfurling (standalone): Error fetching metadata:', error);
                                    setPendingUrls(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(normalizedURL);
                                        return newSet;
                                    });
                                });
                            }, 100); // Small delay to let the link be created first
                            }
                        });

                        return true;
                    }

                    return false;
                },
                COMMAND_PRIORITY_HIGH - 1 // Lower priority than the selection handler
            )
        );
    }, [editor]);

    return null;
}
